import { walletApiSlice } from '../apiSlice';

export interface IServicePricing {
	id: string;
	serviceType: string;
	creditCost: number;
	isUniversal: boolean;
	effectiveFrom: string;
	effectiveTo: string | null;
	createdBy: string;
	createdAt: string;
	isCurrentlyActive: boolean;
}

export interface IConversionRate {
	id: string;
	nairaPerCredit: number;
	effectiveFrom: string;
	createdBy: string;
}

export interface IConversionRateHistoryItem {
	id: string;
	nairaPerCredit: number;
	effectiveFrom: string;
	effectiveTo: string | null;
	createdBy: string;
	createdAt: string;
	isCurrentlyActive: boolean;
}

export interface IConversionRateHistoryRequest {
	fromDate: string | null;
	toDate: string | null;
	pageNumber: number;
	pageSize: number;
}

export interface IConversionRateHistoryState {
	conversionRateHistory: IConversionRateHistoryItem[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export interface IConversionRateHistoryItem {
	id: string;
	nairaPerCredit: number;
	effectiveFrom: string;
	effectiveTo: string | null;
	createdBy: string;
	createdAt: string;
	isCurrentlyActive: boolean;
}

export interface IConversionRateHistoryRequest {
	fromDate: string | null;
	toDate: string | null;
	pageNumber: number;
	pageSize: number;
}

export interface IConversionRateHistoryState {
	conversionRateHistory: IConversionRateHistoryItem[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export interface IServicePricingState {
	servicePricings: IServicePricing[];
	totalCount: number;
	pageNumber: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	pageSize: number;
}

export interface IServicePricingHistoryRequest {
	serviceType: string | null;
	fromDate: string | null;
	toDate: string | null;
	pageNumber: number;
	pageSize: number;
}

export interface IServicePricingHistoryState {
	serviceType: string | null;
	pricingHistory: IServicePricing[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export interface IServicePricingItem {
	id: string;
	serviceType: string;
	creditCost: number;
	isUniversal: boolean;
	effectiveFrom: string | null;
	effectiveTo: string | null;
	createdBy: string;
	createdAt: string;
}

export interface IServicePricingHistoryRequest {
	serviceType: string | null;
	fromDate: string | null;
	toDate: string | null;
	pageNumber: number;
	pageSize: number;
}

export interface IServicePricingHistoryState {
	serviceType: string | null;
	pricingHistory: IServicePricing[];
	totalCount: number;
	pageNumber: number;
	pageSize: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
}

export const pricingApiSlice = walletApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getServicePricing: builder.mutation<
			IServicePricingState,
			{
				pageNumber?: number;
				pageSize?: number;
				activeOnly?: boolean;
				asOfDate?: string | null;
			}
		>({
			query: (body) => ({
				url: '/pricing/service-pricing/search',
				method: 'POST',
				body: {
					pageNumber: body.pageNumber ?? 1,
					pageSize: body.pageSize ?? 50,
					activeOnly: body.activeOnly ?? true,
					asOfDate: body.asOfDate ?? null,
				},
			}),
		}),
		getServicePricingHistory: builder.mutation<IServicePricingHistoryState, IServicePricingHistoryRequest>({
			query: (body) => ({ url: '/pricing/service-pricing/history', method: 'POST', body }),
		}),
		createServicePricing: builder.mutation<
			unknown,
			{
				serviceType: string;
				creditCost: number;
				isUniversal: boolean;
				effectiveFrom: string;
				effectiveTo: string | null;
				createdBy: string;
				id?: string;
			}
		>({
			query: (body) => ({
				url: '/pricing/service-pricing',
				method: 'POST',
				body,
			}),
		}),
		updateServicePricing: builder.mutation<
			IServicePricingItem,
			{
				creditCost: number;
				updatedBy: string;
				id: string;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `/pricing/service-pricing/${id}`,
				method: 'PUT',
				body,
			}),
		}),

		getCurrentConversionRate: builder.query<IConversionRate, void>({
			query: () => '/pricing/conversion-rates/current',
		}),
		getConversionRateHistory: builder.query<IConversionRateHistoryState, IConversionRateHistoryRequest>({
			query: (body) => ({ url: '/pricing/conversion-rates/history', method: 'POST', body }),
		}),
		updateConversionRate: builder.mutation({
			query: (body) => ({
				url: '/pricing/conversion-rates',
				method: 'POST',
				body,
			}),
		}),
		deleteServicePricing: builder.mutation<unknown, { servicePricingId: string; deletedBy: string; deletedReason?: string }>({
			query: ({ servicePricingId, deletedBy, deletedReason }) => ({
				url: `/pricing/service-pricing/${servicePricingId}`,
				method: 'DELETE',
				// Backend validates ServicePricingId from the body (route binding alone
				// fails with 500 "ServicePricingId is required"), so send it explicitly.
				body: {
					servicePricingId,
					deletedBy,
					deletedReason,
				},
			}),
		}),
	}),
});

export const {
	useGetServicePricingMutation,
	useGetServicePricingHistoryMutation,
	useCreateServicePricingMutation,
	useUpdateServicePricingMutation,
	useGetCurrentConversionRateQuery,
	useLazyGetCurrentConversionRateQuery,
	useGetConversionRateHistoryQuery,
	useUpdateConversionRateMutation,
	useDeleteServicePricingMutation,
} = pricingApiSlice;
